import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

// CSS styling
import "../../styles/activitiesPublic.css";
import "../../styles/clientBook.css";

// HTML react parser
import parse, { attributesToProps, domToReact } from "html-react-parser";

// react responsive
import { useMediaQuery } from "react-responsive";

// moment js
import moment from "moment";

// helpers
import { absoluteUrl, isoDate, plainText } from "../../helpers/seo";

// apis
import { fetchBookDetails } from "../../api/books.js";

// Components
import Seo, { organizationRef } from "../../components/Seo.js";
import Header from "../../components/Header.js";
import Footer from "../../components/Footer.js";
import ContainerText from "../../components/Container.js";

// React lottie
import Lottie from "react-lotties";
import PaperPlane from "../../lotties/paperplane.json";

// The page already has its own <h1>, so an <h1> written in the CMS content becomes an <h2> that looks the same
const parseContent = (html) =>
  parse(html, {
    replace: (node) => {
      if (node.type === "tag" && node.name === "h1") {
        return (
          <h2 {...attributesToProps(node.attribs)} className="h1">
            {domToReact(node.children)}
          </h2>
        );
      }
    },
  });

const BookDetails = () => {
  const { book_id } = useParams();

  const isMobileSize = useMediaQuery({ query: "(max-width: 680px)" });
  const isTabSize = useMediaQuery({
    query: "(min-width: 681px) and (max-width: 910px)",
  });

  const [bookDetails, setBookDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // React lotties
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PaperPlane,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const handleFetchBookDetails = async (book_id) => {
    setLoading(true);
    setNotFound(false);
    try {
      const result = await fetchBookDetails(book_id);
      if (result && result.data) {
        setBookDetails(result.data);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.log(err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadEbookPath = (book_name) =>
    `/books/download-file-form/${book_name}`;

  useEffect(() => {
    if (book_id) {
      handleFetchBookDetails(book_id);
    }
  }, [book_id]);

  const seoPath = `/books/${book_id}`;
  const bookLanguage =
    bookDetails && bookDetails.language === "English" ? "en" : "id";
  const seoDescription = bookDetails
    ? plainText(bookDetails.description || bookDetails.what_you_will_learn, 160)
    : "";
  const bookAuthors =
    bookDetails && Array.isArray(bookDetails.book_authors)
      ? bookDetails.book_authors
          .filter((author) => author && author.author_name)
          .map((author) => ({ "@type": "Person", name: author.author_name }))
      : [];

  return (
    <div>
      {bookDetails && !loading ? (
        <Seo
          title={bookDetails.title}
          description={seoDescription}
          path={seoPath}
          image={bookDetails.img_url}
          imageAlt={bookDetails.title}
          lang={bookLanguage}
          breadcrumbs={[
            { name: "Books", path: "/books" },
            { name: bookDetails.title, path: seoPath },
          ]}
          jsonLd={{
            "@type": "Book",
            name: bookDetails.title,
            url: absoluteUrl(seoPath),
            author: bookAuthors.length ? bookAuthors : undefined,
            image: bookDetails.img_url
              ? absoluteUrl(bookDetails.img_url)
              : undefined,
            description: seoDescription,
            publisher: organizationRef,
            inLanguage: bookLanguage,
            isbn: bookDetails.isbn || undefined,
            numberOfPages: bookDetails.number_of_pages || undefined,
            datePublished: isoDate(bookDetails.published_on),
          }}
        />
      ) : notFound && !loading ? (
        <Seo title="Book not found" noindex />
      ) : null}
      <Header />
      <div className="activityPublicContainer">
        {loading ? (
          <div
            style={{
              paddingTop: "10em",
              paddingBottom: "5em",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Lottie options={lottieOptions} height={200} width={400} />
            <h4
              style={{
                color: "#0D4C86",
              }}
            >
              LOADING
            </h4>
            <p
              style={{
                color: "#0D4C86",
              }}
            >
              Processing your request, please wait...
            </p>
          </div>
        ) : bookDetails ? (
          <>
            <div className="bookDetailTitleContainer title">
              <h1 className="seoH1 seoH1Inherit">
                {bookDetails && bookDetails.title}
              </h1>
            </div>
            <ContainerText
              props={
                <div className="bookDetailsMainContainerDesc">
                  {isMobileSize ? (
                    <>
                      <div className="bookDetailsMainImageMainContainer">
                        <div className="detailBookImageContainer">
                          <img
                            src={bookDetails && bookDetails.img_url}
                            alt={bookDetails.title}
                          />
                        </div>
                        <div
                          className="eachClientBookPublishedDateStyle paragrafDesc"
                          style={{
                            fontWeight: 700,
                            textAlign: "left",
                            paddingTop: "1em",
                          }}
                        >
                          {`Published on: ${
                            bookDetails.published_on
                              ? moment(bookDetails.published_on).format(
                                  "DD/MM/YYYY",
                                )
                              : "-"
                          }`}
                        </div>
                        <div
                          className="eachClientBookPublishedDateStyle paragrafDesc"
                          style={{
                            fontWeight: 700,
                            textAlign: "left",
                          }}
                        >
                          {`Number of Pages: ${bookDetails.number_of_pages}`}
                        </div>
                        {bookDetails.book_authors &&
                        Array.isArray(bookDetails.book_authors) &&
                        bookDetails.book_authors.length > 0 ? (
                          <div>
                            <div
                              className="eachClientBookPublishedDateStyle paragrafDesc"
                              style={{
                                fontWeight: 700,
                                textAlign: "left",
                              }}
                            >
                              {`Author${
                                bookDetails.book_authors.length > 1 ? "(s)" : ""
                              }:`}
                            </div>
                            <ul style={{ textAlign: "left" }}>
                              {bookDetails.book_authors.map((book_author) => {
                                const { id, author_name } = book_author || {};
                                return (
                                  <li key={id}>
                                    <div className="text">{author_name}</div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                        <div
                          className="eachClientBookPublishedDateStyle paragrafDesc"
                          style={{
                            fontWeight: 700,
                            textAlign: "left",
                            marginTop:
                              bookDetails.book_authors &&
                              Array.isArray(bookDetails.book_authors) &&
                              bookDetails.book_authors.length > 0
                                ? "-12.5px"
                                : "",
                          }}
                        >
                          {`ISBN: ${bookDetails.isbn ? bookDetails.isbn : "-"}`}
                        </div>
                        <Link
                          className="downloadBookContainer"
                          to={downloadEbookPath(
                            bookDetails && bookDetails.title,
                          )}
                          state={{ isBook: true }}
                          style={{
                            display: "block",
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          <div>Download E-Book</div>
                        </Link>
                      </div>
                      <div className="bookDetailsWhatWillYouLearnContainer">
                        <div
                          className="title"
                          style={{
                            color: "#1F628E",
                            fontWeight: "600",
                          }}
                        >
                          What will you learn from this book:
                        </div>
                        {bookDetails && bookDetails.what_you_will_learn ? (
                          <div className="bookDetailsWhatYouWillLearnStyle paragraf">
                            {parseContent(bookDetails.what_you_will_learn)}
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bookDetailsWhatWillYouLearnContainer">
                        <div
                          className="title"
                          style={{
                            color: "#1F628E",
                            fontWeight: "600",
                          }}
                        >
                          What will you learn from this book:
                        </div>
                        {bookDetails && bookDetails.what_you_will_learn ? (
                          <div className="bookDetailsWhatYouWillLearnStyle paragraf">
                            {parseContent(bookDetails.what_you_will_learn)}
                          </div>
                        ) : null}
                      </div>
                      <div className="bookDetailsMainImageMainContainer">
                        <div className="detailBookImageContainer">
                          <img
                            src={bookDetails && bookDetails.img_url}
                            alt={bookDetails.title}
                          />
                        </div>
                        <div
                          className="eachClientBookPublishedDateStyle paragrafDesc"
                          style={{
                            fontWeight: 700,
                            textAlign: "left",
                            paddingTop: "1em",
                          }}
                        >
                          {`Published on: ${
                            bookDetails.published_on
                              ? moment(bookDetails.published_on).format(
                                  "DD/MM/YYYY",
                                )
                              : "-"
                          }`}
                        </div>
                        <div
                          className="eachClientBookPublishedDateStyle paragrafDesc"
                          style={{
                            fontWeight: 700,
                            textAlign: "left",
                          }}
                        >
                          {`Number of Pages: ${bookDetails.number_of_pages}`}
                        </div>
                        {bookDetails.book_authors &&
                        Array.isArray(bookDetails.book_authors) &&
                        bookDetails.book_authors.length > 0 ? (
                          <div>
                            <div
                              className="eachClientBookPublishedDateStyle paragrafDesc"
                              style={{
                                fontWeight: 700,
                                textAlign: "left",
                              }}
                            >
                              {`Author${
                                bookDetails.book_authors.length > 1 ? "(s)" : ""
                              }:`}
                            </div>
                            <ul
                              style={{
                                textAlign: "left",
                                fontSize: "14px",
                              }}
                            >
                              {bookDetails.book_authors.map((book_author) => {
                                const { id, author_name } = book_author || {};
                                return (
                                  <li key={id}>
                                    <div className="text">{author_name}</div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                        <div
                          className="eachClientBookPublishedDateStyle paragrafDesc"
                          style={{
                            fontWeight: 700,
                            textAlign: "left",
                            marginTop:
                              bookDetails.book_authors &&
                              Array.isArray(bookDetails.book_authors) &&
                              bookDetails.book_authors.length > 0
                                ? "-12.5px"
                                : "",
                          }}
                        >
                          {`ISBN: ${bookDetails.isbn ? bookDetails.isbn : "-"}`}
                        </div>
                        <Link
                          className="downloadBookContainer"
                          to={downloadEbookPath(
                            bookDetails && bookDetails.title,
                          )}
                          state={{ isBook: true }}
                          style={{
                            display: "block",
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          <div>Download E-Book</div>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              }
            />
          </>
        ) : null}
      </div>
      <Footer />
    </div>
  );
};

export default BookDetails;
